import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import {
  DesignerRequestVerb,
  ISetFileContentRequest,
  ISetFileContentResponse,
  WebformStateCondition,
} from '@qodly/shared';
import { API_PREFIX, http, queue } from '@qodly/shared';
import { fetchSavedConditions } from './thunks';
import { generate } from 'short-uuid';
import { updateDsCondition } from './utils';
import { editWebformState, removeWebformState } from '../root/reducer';
import { refactorConditions } from '~store/utils';

export type ISavedConditionsState = {
  state: 'loading' | 'loaded' | 'error' | 'unknown';
  conditions: WebformStateCondition[];
  error?: string;
};

const conditionsQueue = queue();

async function setSavedConditions(conditions: WebformStateCondition[]) {
  const payload: ISetFileContentRequest = {
    verb: DesignerRequestVerb.SET_FILE_CONTENT,
    asText: false,
    name: 'saved_conditions.json',
    path: '',
    root: 'shared',
    content: {
      conditions,
    },
  };

  await conditionsQueue(() =>
    http<ISetFileContentResponse>(API_PREFIX, {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: new Headers({
        'Content-Type': 'application/json',
      }),
    }),
  );
}

export const deepDeleteCondition = (
  condition: WebformStateCondition,
  targetId: string,
) => {
  if (condition.id === targetId) {
    return null;
  } else if (condition.type === 'reference' && condition.ref === targetId) {
    return null;
  } else if (condition.type === 'combination' && condition.conditions) {
    const conditions = condition.conditions
      .map((c) => deepDeleteCondition(c, targetId))
      .filter(Boolean) as WebformStateCondition[];

    // when a combination contains only one condition
    // suppress the combination & keep only the condition
    if (conditions.length === 1) {
      return {
        ...conditions[0],
        id: condition.id,
      };
    }

    return {
      ...condition,
      conditions,
    };
  }
  return condition;
};

export const deepSanitizeCondition = (
  condition: WebformStateCondition,
): WebformStateCondition => {
  if (!condition.id) {
    return {
      ...condition,
      id: generate(),
    };
  } else if (condition.type === 'combination' && condition.conditions) {
    return {
      ...condition,
      conditions: condition.conditions.map(deepSanitizeCondition),
    };
  }
  return condition;
};

const stateSlice = createSlice({
  name: 'shared-conditions',
  initialState: { state: 'unknown', conditions: [] } as ISavedConditionsState,
  reducers: {
    updateSavedConditions: (
      state,
      action: PayloadAction<{ conditions: WebformStateCondition[] }>,
    ) => {
      state.conditions = action.payload.conditions;

      setSavedConditions(action.payload.conditions);

      return state;
    },
    setSavedCondition: (
      state,
      action: PayloadAction<{ id: string; condition: WebformStateCondition }>,
    ) => {
      const { payload } = action;
      state.conditions = state.conditions.map((condition) => {
        if (condition.id === payload.id) {
          return {
            ...deepSanitizeCondition(payload.condition),
            // ensure to keep the original id, otherwise it will break all the existing refs to this id
            id: condition.id,
          };
        }
        return condition;
      });

      setSavedConditions(state.conditions);

      return state;
    },
    updateSavedConditionDs: (
      state,
      action: PayloadAction<{
        old_reference: string;
        new_reference: string;
      }>,
    ) => {
      const { old_reference, new_reference } = action.payload;

      state.conditions = state.conditions.map((condition) => {
        return updateDsCondition(condition, old_reference, new_reference);
      });

      setSavedConditions(state.conditions);
    },
    addSavedCondition: (
      state,
      action: PayloadAction<WebformStateCondition>,
    ) => {
      state.conditions = [...state.conditions, action.payload];

      setSavedConditions(state.conditions);

      return state;
    },
    removeSavedCondition: (state, action: PayloadAction<{ id: string }>) => {
      const { id } = action.payload;

      state.conditions = state.conditions
        .map((condition) => deepDeleteCondition(condition, id))
        .filter(Boolean) as WebformStateCondition[];

      setSavedConditions(state.conditions);

      return state;
    },
    initSavedConditions: (
      state,
      action: PayloadAction<{ conditions: WebformStateCondition[] }>,
    ) => {
      state.conditions = action.payload.conditions;

      return state;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchSavedConditions.pending, (state) => {
        return {
          ...state,
          state: 'loading',
        };
      })
      .addCase(fetchSavedConditions.fulfilled, (_state, { payload }) => {
        return {
          state: 'loaded',
          conditions: payload,
        };
      })
      .addCase(fetchSavedConditions.rejected, (state, action) => {
        return {
          ...state,
          state: 'error',
          error: action.payload || 'Error while loading the shared conditions',
        };
      });
    builder.addMatcher(isAnyOf(editWebformState), (state, action) => {
      const { state: webformState, oldLabel } = action.payload;
      state.conditions = state.conditions.map((condition) => {
        return refactorConditions(condition, oldLabel, webformState.label);
      });
    });
    builder.addMatcher(isAnyOf(removeWebformState), (state, action) => {
      const { payload } = action;
      state.conditions = state.conditions.map((condition) => {
        return refactorConditions(condition, payload.state?.label);
      });
    });
  },
});

export const {
  updateSavedConditions,
  setSavedCondition,
  updateSavedConditionDs,
  addSavedCondition,
  removeSavedCondition,
  initSavedConditions,
} = stateSlice.actions;

export default stateSlice;
